import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { MachineLink } from '@/components/machines';
import { SummaryMetricCard, MetricGrid } from '@/components/global/SummaryMetricCard';
import { DateRangePicker, useDateRange } from '@/components/finance';
import { WifiOff, Clock, Hash, AlertTriangle, Download } from 'lucide-react';
import { downloadCsv } from '@/lib/csvExport';

interface DowntimeReportItem {
  machineId: string;
  machineName: string;
  venueName: string | null;
  totalDowntimeMinutes: number;
  totalDowntimeHours: number;
  eventCount: number;
  averageDurationMinutes: number;
  longestDurationMinutes: number;
  lastDowntimeAt: string | null;
  currentlyDown: boolean;
}

interface DowntimeReport {
  period: { startDate: string; endDate: string };
  items: DowntimeReportItem[];
  summary: {
    totalMachines: number;
    totalDowntimeHours: number;
    totalEvents: number;
    currentlyDownCount: number;
    averageDowntimePerMachine: number;
  };
}

function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }
  const hours = minutes / 60;
  if (hours < 24) {
    return `${hours.toFixed(1)}h`;
  }
  return `${(hours / 24).toFixed(1)}d`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function DowntimeReportPage() {
  const { dateRange, setDateRange } = useDateRange('L30D');
  const [report, setReport] = useState<DowntimeReport | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
      });

      const res = await apiFetch(`/api/v1/reports/downtime?${params}`);
      if (res.ok) {
        const data = await res.json();
        setReport(data);
      }
    } catch (error) {
      console.error('Error loading downtime report:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function exportCsv() {
    if (!report || report.items.length === 0) return;
    downloadCsv(
      `downtime-report-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Machine',
        'Venue',
        'Events',
        'Total downtime (minutes)',
        'Avg duration (minutes)',
        'Longest (minutes)',
        'Last downtime',
        'Currently down',
      ],
      report.items.map((item) => [
        item.machineName,
        item.venueName ?? '',
        item.eventCount,
        Math.round(item.totalDowntimeMinutes),
        Math.round(item.averageDurationMinutes),
        Math.round(item.longestDurationMinutes),
        item.lastDowntimeAt ?? '',
        item.currentlyDown ? 'Yes' : 'No',
      ])
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Downtime Report</h1>
          <div className="flex items-center gap-2">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={loading || !report || report.items.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Machines Affected"
            value={report ? report.summary.totalMachines.toString() : '-'}
            icon={WifiOff}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Total Downtime"
            value={report ? `${report.summary.totalDowntimeHours.toFixed(1)}h` : '-'}
            icon={Clock}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Total Events"
            value={report ? report.summary.totalEvents.toString() : '-'}
            icon={Hash}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Currently Down"
            value={report ? report.summary.currentlyDownCount.toString() : '-'}
            icon={AlertTriangle}
            status={report && report.summary.currentlyDownCount > 0 ? 'red' : undefined}
            isLoading={loading}
          />
        </MetricGrid>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Machine</TableHead>
                <TableHead>Venue</TableHead>
                <TableHead className="text-right">Events</TableHead>
                <TableHead className="text-right">Total Downtime</TableHead>
                <TableHead className="text-right">Avg Duration</TableHead>
                <TableHead className="text-right">Longest</TableHead>
                <TableHead>Last Downtime</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : report?.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No downtime events found for this period
                  </TableCell>
                </TableRow>
              ) : (
                report?.items.map((item) => (
                  <TableRow key={item.machineId}>
                    <TableCell>
                      <MachineLink machineId={item.machineId} name={item.machineName} />
                    </TableCell>
                    <TableCell>{item.venueName || '-'}</TableCell>
                    <TableCell className="text-right">{item.eventCount}</TableCell>
                    <TableCell className="text-right">{formatDuration(item.totalDowntimeMinutes)}</TableCell>
                    <TableCell className="text-right">{formatDuration(item.averageDurationMinutes)}</TableCell>
                    <TableCell className="text-right">{formatDuration(item.longestDurationMinutes)}</TableCell>
                    <TableCell>{formatDate(item.lastDowntimeAt)}</TableCell>
                    <TableCell>
                      {item.currentlyDown ? (
                        <Badge variant="destructive">Offline</Badge>
                      ) : (
                        <Badge variant="outline">Online</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
